import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { QUESTIONS, SECTIONS, computeScores, computeDominant, scoreLabel } from "./quizData";
import QuizResults from "./QuizResults.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE = {
  LANDING: "landing",
  WELCOME: "welcome",
  TRANSITION: "transition", // uses transitionIndex
  QUESTION: "question", // uses questionIndex (0..15)
  GATE: "gate",
  PROCESSING: "processing",
  RESULTS: "results",
};

/* ---------- Reusable shell ---------- */
function MinimalHeader() {
  return <Nav heroVisible={false} />;
}

function ProgressBar({ current, total }) {
  const pct = (current / total) * 100;
  return (
    <div className="fixed top-0 inset-x-0 z-30">
      <div className="h-[3px] w-full bg-stonegrey/25">
        <div
          className="h-full bg-camel transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="max-w-[720px] mx-auto px-6 md:px-10 mt-2">
        <p
          className="uppercase text-stonegrey"
          style={{ fontSize: "10.5px", letterSpacing: "0.22em", fontWeight: 500 }}
        >
          Question {current} of {total}
        </p>
      </div>
    </div>
  );
}

/* ---------- Stage: Landing ---------- */
function Landing({ onBegin }) {
  return (
    <div className="min-h-screen bg-paper text-nearblack relative">
      <MinimalHeader />
      <main className="pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="max-w-[720px] mx-auto px-6 md:px-10 text-center">
          <p
            className="uppercase text-stonegrey"
            style={{ fontSize: "11px", letterSpacing: "0.12em", fontWeight: 500 }}
          >
            Liz DiRocco · Strategic Banking. Infinite Possibilities.
          </p>
          <h1
            className="font-serif text-nearblack mt-8 leading-[1.1] text-balance"
            style={{ fontSize: "clamp(2.5rem, 6.5vw, 4rem)", fontWeight: 300 }}
            data-testid="landing-headline"
          >
            You&apos;re making money.
            <br />
            <em className="italic text-camel" style={{ fontWeight: 300 }}>
              But are you building wealth?
            </em>
          </h1>
          <span className="block w-[60px] h-px bg-camel mx-auto mt-10" />
          <p className="mt-10 text-nearblack font-light text-[19px] leading-[1.7]">
            Most high earners aren&apos;t failing because they aren&apos;t smart.
            <br />
            They&apos;re failing because of one invisible financial pattern —
            <br />
            quietly running every decision they make.
            <br />
            <br />
            <span
              className="text-camel font-serif italic block mt-3"
              style={{ fontSize: "clamp(1.3rem, 2.2vw, 1.55rem)", lineHeight: 1.4 }}
            >
              This assessment reveals yours.
            </span>
          </p>

          <div className="mt-14 text-nearblack/85 font-light text-[18px] leading-[1.8] max-w-[620px] mx-auto">
            <p>
              The Wealth Identity Assessment is a 4-minute financial diagnostic built for people
              who are already earning well — and still feel like they should be further ahead.
            </p>
            <p className="mt-5 italic font-serif text-nearblack/80">
              It isn&apos;t a quiz about budgeting. It isn&apos;t a checklist of mistakes.
            </p>
            <p className="mt-5 font-serif italic text-camel" style={{ fontSize: "clamp(1.6rem, 2.6vw, 1.85rem)", lineHeight: 1.3 }}>
              It&apos;s a mirror.
            </p>
            <p className="mt-5">
              By the time you finish, you&apos;ll understand exactly which wealth pattern has been
              quietly shaping your financial life — and what it&apos;s been costing you.
            </p>
          </div>

          <div className="mt-14 text-left max-w-[560px] mx-auto">
            <p
              className="uppercase text-stonegrey mb-5"
              style={{ fontSize: "10.5px", letterSpacing: "0.22em", fontWeight: 500 }}
            >
              What you&apos;ll discover
            </p>
            <ul className="space-y-4 text-nearblack/85 font-light text-[16.5px] leading-[1.75]">
              {[
                "The one financial pattern that explains most of your money decisions",
                "Why your income hasn't translated into the wealth you expected",
                "What your financial blind spot is actually costing you",
                "The single most important shift available to you right now",
              ].map((t, i) => (
                <li key={i} className="flex gap-4">
                  <span className="text-camel mt-2 block w-6 h-px flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-14 text-stonegrey italic text-[15px]">
            Built for people earning $75K–$300K+ who know something isn&apos;t adding up.
          </p>

          <button
            type="button"
            onClick={onBegin}
            data-testid="quiz-begin"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors"
            style={{
              height: "56px",
              width: "min(400px, 100%)",
              fontSize: "17px",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            Begin the Assessment <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-4 text-stonegrey text-[12px]">
            Free. 4 minutes. No generic advice.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Stage: Welcome ---------- */
function Welcome({ onContinue }) {
  return (
    <div className="min-h-screen bg-paper text-nearblack relative">
      <MinimalHeader />
      <main className="pt-36 md:pt-44 pb-24">
        <div className="max-w-[640px] mx-auto px-6 md:px-10">
          <h1
            className="font-serif text-nearblack leading-[1.1]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 2.85rem)", fontWeight: 400 }}
          >
            Before we begin.
          </h1>
          <span className="block w-[60px] h-px bg-camel mt-8 mb-10" />
          <div className="text-nearblack font-light text-[18px] leading-[1.75] space-y-5">
            <p>This isn&apos;t a personality quiz.</p>
            <p>
              It&apos;s a diagnostic tool designed to identify the one financial pattern that&apos;s
              been quietly shaping how you earn, spend, protect, and grow money — often without
              realizing it.
            </p>
            <p>There are no wrong answers.</p>
            <p>
              Answer based on how things <em>actually</em> are — not how you think they should be,
              or how you wish they were. The more honest you are, the more accurate your results
              will be.
            </p>
            <p>
              You&apos;ll answer 16 questions across four areas.
              <br />
              It takes about 4 minutes.
            </p>
            <p className="font-serif italic text-stonegrey">
              What you find out may be uncomfortable. It will be useful.
            </p>
          </div>
          <button
            type="button"
            onClick={onContinue}
            data-testid="quiz-welcome-ready"
            className="mt-12 inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors"
            style={{
              height: "56px",
              width: "min(400px, 100%)",
              fontSize: "17px",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            I&apos;m ready <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}

/* ---------- Stage: Transition ---------- */
function Transition({ section, onContinue, current, total }) {
  return (
    <div className="min-h-screen bg-paper text-nearblack relative">
      <ProgressBar current={current} total={total} />
      <MinimalHeader />
      <main className="pt-36 md:pt-44 pb-24">
        <div className="max-w-[560px] mx-auto px-6 md:px-10 text-center">
          <p
            className="uppercase text-stonegrey"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            {section.label}
          </p>
          <h2
            className="font-serif text-nearblack mt-7 leading-[1.1] whitespace-pre-line"
            style={{ fontSize: "clamp(1.75rem, 3.6vw, 2.4rem)", fontWeight: 400 }}
          >
            {section.heading}
          </h2>
          <span className="block w-[60px] h-px bg-camel mt-10 mx-auto" />
          <p className="mt-10 text-stonegrey font-light text-[17px] leading-[1.7] whitespace-pre-line">
            {section.body}
          </p>
          <button
            type="button"
            onClick={onContinue}
            data-testid={`quiz-transition-continue-${section.index}`}
            className="mt-12 inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors mx-auto"
            style={{
              height: "56px",
              width: "min(400px, 100%)",
              fontSize: "17px",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}

/* ---------- Stage: Question ---------- */
function Question({ index, total, question, selected, onSelect, onNext, onBack }) {
  return (
    <div className="min-h-screen bg-paper text-nearblack relative">
      <ProgressBar current={index + 1} total={total} />
      <MinimalHeader />
      <main className="pt-32 md:pt-40 pb-32">
        <div className="max-w-[720px] mx-auto px-6 md:px-10">
          <p
            className="uppercase text-stonegrey"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            Scenario
          </p>
          <h2
            className="font-serif text-nearblack mt-5 leading-[1.25]"
            style={{ fontSize: "clamp(1.4rem, 2.8vw, 1.85rem)", fontWeight: 400 }}
            data-testid={`question-prompt-${index + 1}`}
          >
            {question.prompt}
          </h2>
          <div className="mt-10 space-y-4">
            {question.options.map((opt, i) => {
              const [text, score, microValidation] = opt;
              const isSelected = selected === i;
              return (
                <div key={i}>
                  <button
                    type="button"
                    onClick={() => onSelect(i)}
                    data-testid={`question-${index + 1}-option-${i + 1}`}
                    className={`w-full text-left p-5 md:p-6 transition-colors border-[1.5px] ${
                      isSelected
                        ? "border-camel bg-camellight/30"
                        : "border-paperdeep bg-white hover:border-camel/60"
                    }`}
                    style={{ minHeight: "52px", fontSize: "16px", lineHeight: 1.55, fontWeight: 400 }}
                  >
                    <span className="text-nearblack">{text}</span>
                    <span className="sr-only"> (score {score})</span>
                  </button>
                  {isSelected && (
                    <p
                      className="text-stonegrey italic mt-2 px-1 transition-opacity"
                      style={{ fontSize: "13px", animation: "quizFadeIn .4s ease-out" }}
                      data-testid={`question-${index + 1}-microvalidation`}
                    >
                      {microValidation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-14 flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              data-testid={`question-${index + 1}-back`}
              disabled={index === 0}
              className="inline-flex items-center gap-2 text-stonegrey hover:text-nearblack transition-colors disabled:opacity-30 disabled:hover:text-stonegrey uppercase tracking-[0.22em]"
              style={{ fontSize: "11px", fontWeight: 500 }}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={selected === null}
              data-testid={`question-${index + 1}-next`}
              className="inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                height: "52px",
                padding: "0 28px",
                fontSize: "16px",
                fontWeight: 600,
                borderRadius: "8px",
              }}
            >
              {index + 1 === total ? "Reveal Results" : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
      <style>{`@keyframes quizFadeIn { from { opacity: 0; transform: translateY(2px);} to {opacity:1; transform: translateY(0);} }`}</style>
    </div>
  );
}

/* ---------- Stage: Email Gate ---------- */
function EmailGate({ loading, onSubmit }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    onSubmit(firstName.trim(), email.trim());
  };
  return (
    <div className="min-h-screen bg-nearblack text-paper relative flex flex-col">
      <main className="flex-1 flex items-center justify-center py-20 px-6">
        <div className="w-full max-w-[520px]">
          <p
            className="uppercase text-camel text-center"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            ✦ Your Results Are Ready ✦
          </p>
          <h2
            className="font-serif text-paper mt-7 leading-[1.05] text-center"
            style={{ fontSize: "clamp(2rem, 4.5vw, 2.75rem)", fontWeight: 300 }}
          >
            Your Wealth Pattern
            <br />
            has been identified.
          </h2>
          <p className="mt-7 text-paper/85 text-[16.5px] leading-[1.7] font-light text-center max-w-[440px] mx-auto">
            In a moment, you&apos;ll see your full breakdown across all four wealth areas — and the
            one pattern that&apos;s had the most influence on your financial life.
          </p>
          <p className="mt-4 text-paper/70 text-[14.5px] leading-[1.7] font-light text-center max-w-[440px] mx-auto">
            Enter your name and email to reveal your results and receive a copy directly in your inbox.
          </p>

          <form
            onSubmit={submit}
            noValidate
            data-testid="quiz-gate-form"
            className="mt-10 bg-paper p-6 md:p-7 text-nearblack"
            style={{ borderRadius: "8px" }}
          >
            <label
              className="block uppercase text-stonegrey"
              style={{ fontSize: "10.5px", letterSpacing: "0.22em", fontWeight: 500 }}
            >
              First Name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
              data-testid="quiz-gate-first-name"
              placeholder="Your first name"
              className="w-full mt-2 bg-white border border-paperdeep px-4 text-[16px] text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
              style={{ height: "48px" }}
            />
            <label
              className="block uppercase text-stonegrey mt-5"
              style={{ fontSize: "10.5px", letterSpacing: "0.22em", fontWeight: 500 }}
            >
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              data-testid="quiz-gate-email"
              placeholder="you@email.com"
              className="w-full mt-2 bg-white border border-paperdeep px-4 text-[16px] text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
              style={{ height: "48px" }}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="quiz-gate-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-camel hover:bg-camellight text-nearblack transition-colors disabled:opacity-60"
              style={{ height: "56px", fontSize: "17px", fontWeight: 600, borderRadius: "8px" }}
            >
              {loading ? "Loading..." : "Show My Results"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="mt-4 text-stonegrey text-center" style={{ fontSize: "12px" }}>
              No spam. Just your results and one clear next step. Unsubscribe anytime.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

/* ---------- Stage: Processing ---------- */
function Processing({ onDone }) {
  const [idx, setIdx] = useState(0);
  const lines = [
    "Analyzing your responses...",
    "Identifying your dominant pattern...",
    "Your results are ready.",
  ];
  useEffect(() => {
    const t1 = setTimeout(() => setIdx(1), 1300);
    const t2 = setTimeout(() => setIdx(2), 2600);
    const t3 = setTimeout(onDone, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);
  return (
    <div className="min-h-screen bg-nearblack text-paper flex items-center justify-center px-6" data-testid="quiz-processing">
      <div className="text-center w-full max-w-[560px]">
        <p
          key={idx}
          className="font-serif text-paper transition-opacity"
          style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 300, animation: "quizFade .9s ease-out" }}
        >
          {lines[idx]}
        </p>
        <div className="relative mt-12 h-px w-full bg-paper/15 overflow-hidden">
          <div
            className="absolute left-1/2 -translate-x-1/2 h-full bg-camel"
            style={{ animation: "quizGoldGrow 4s linear forwards" }}
          />
        </div>
      </div>
      <style>{`
        @keyframes quizFade { from {opacity:0;} to {opacity:1;} }
        @keyframes quizGoldGrow { from {width:0;} to {width:100%;} }
      `}</style>
    </div>
  );
}

/* ---------- Main quiz controller ---------- */
export default function QuizPage() {
  const [stage, setStage] = useState(STAGE.LANDING);
  const [transitionIndex, setTransitionIndex] = useState(0); // 0..3
  const [questionIndex, setQuestionIndex] = useState(0); // 0..15
  const [answers, setAnswers] = useState(Array(16).fill(null)); // null or 1..4
  const [submitLoading, setSubmitLoading] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const submittedRef = useRef(false);

  const total = 16;

  /* Navigation helpers */
  const goWelcome = () => setStage(STAGE.WELCOME);
  const goTransition = (idx) => {
    setTransitionIndex(idx);
    setStage(STAGE.TRANSITION);
    window.scrollTo(0, 0);
  };
  const goQuestion = (idx) => {
    setQuestionIndex(idx);
    setStage(STAGE.QUESTION);
    window.scrollTo(0, 0);
  };

  const handleAnswer = (idx) => (selectedOptionIdx) => {
    const score = QUESTIONS[idx].options[selectedOptionIdx][1];
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = score;
      return next;
    });
  };

  const handleNext = () => {
    const nextIdx = questionIndex + 1;
    // After last question of each section (3, 7, 11), advance to next transition
    if (nextIdx % 4 === 0) {
      const nextTransition = nextIdx / 4; // 1,2,3,4
      if (nextTransition >= 4) {
        setStage(STAGE.GATE);
        window.scrollTo(0, 0);
      } else {
        goTransition(nextTransition);
      }
    } else {
      goQuestion(nextIdx);
    }
  };

  const handleBack = () => {
    if (questionIndex === 0) return;
    goQuestion(questionIndex - 1);
  };

  const selectedOptionForCurrent = (() => {
    const s = answers[questionIndex];
    if (s === null) return null;
    const opts = QUESTIONS[questionIndex].options;
    return opts.findIndex((o) => o[1] === s);
  })();

  const handleGateSubmit = async (firstName, email) => {
    if (submittedRef.current) return;
    setSubmitLoading(true);
    try {
      const numericAnswers = answers.map((a) => (a === null ? 1 : a));
      const { s1, s2, s3, s4 } = computeScores(numericAnswers);
      const dominant = computeDominant(numericAnswers);
      const payload = {
        first_name: firstName,
        email,
        section1_score: s1,
        section2_score: s2,
        section3_score: s3,
        section4_score: s4,
        section1_label: scoreLabel(s1),
        section2_label: scoreLabel(s2),
        section3_label: scoreLabel(s3),
        section4_label: scoreLabel(s4),
        dominant,
        answers: numericAnswers,
      };
      await axios.post(`${API}/quiz`, payload);
      submittedRef.current = true;
      const data = { scores: { s1, s2, s3, s4 }, dominant, firstName };
      setResultsData(data);
      try { sessionStorage.setItem("wia_results", JSON.stringify(data)); } catch { /* noop */ }
      setStage(STAGE.PROCESSING);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your email.");
      setSubmitLoading(false);
    }
  };

  /* Render */
  switch (stage) {
    case STAGE.LANDING:
      return <Landing onBegin={goWelcome} />;
    case STAGE.WELCOME:
      return <Welcome onContinue={() => goTransition(0)} />;
    case STAGE.TRANSITION:
      return (
        <Transition
          section={SECTIONS[transitionIndex]}
          onContinue={() => goQuestion(transitionIndex * 4)}
          current={transitionIndex * 4 + 1}
          total={total}
        />
      );
    case STAGE.QUESTION:
      return (
        <Question
          index={questionIndex}
          total={total}
          question={QUESTIONS[questionIndex]}
          selected={selectedOptionForCurrent}
          onSelect={handleAnswer(questionIndex)}
          onNext={handleNext}
          onBack={handleBack}
        />
      );
    case STAGE.GATE:
      return <EmailGate loading={submitLoading} onSubmit={handleGateSubmit} />;
    case STAGE.PROCESSING:
      return <Processing onDone={() => setStage(STAGE.RESULTS)} />;
    case STAGE.RESULTS:
      return resultsData ? (
        <div className="min-h-screen">
          <MinimalHeader />
          <QuizResults
            scores={resultsData.scores}
            dominant={resultsData.dominant}
            firstName={resultsData.firstName}
          />
        </div>
      ) : null;
    default:
      return null;
  }
}
